import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavigationContainer, DefaultTheme, DarkTheme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createStackNavigator } from '@react-navigation/stack';
import { ColorSchemeName, StatusBar, Dimensions, Platform, Pressable, Image, ScrollView, FlatList, useWindowDimensions, Linking } from 'react-native';
import { LoginStackParamList, RootStackParamList } from './types';
import { View, Text } from '@constants/Themed';
import LinkingConfiguration from './LinkingConfiguration';
import { useDispatch, useSelector } from 'react-redux';
import AuthModal from '../components/modals/ErrorModal/AuthModal';
import DashboardNavigator from './NavigationStacks/DashStack'; // Your Dashboard/App Stack
import LoadingModal from '../components/modals/LoadingModal/LoadingModal';
import LoadingScreen from './screens/LoadingScreen/loadingScreen';
import LoginScreen from './screens/TopLevel/LoginScreen/LoginScreen';
import styles from '../../stylesheet';
import Registration from "./screens/TopLevel/Registration";
import { USER_API_BASE_URL } from '@api/constant';

import RegisterModal from '../components/modals/RegisterModal/RegisterModal';
import { StoreStackNavigator } from './NavigationStacks/StoreStack'; // Another potential app stack
import NotFoundScreen from './screens/TopLevel/NotFoundScreen';
import TCModal from '../components/modals/TCModal/TCModal';
import FormScreen from './Form/FormScreen';
import UserProfileScreen from './screens/IndexLevel/UserProfileDisplay/UserProfileScreen';
import LiveReports from './screens/IndexLevel/LiveReportScreen/LiveReports';
import { CHANGE_LANGUAGE, CHANGE_THEME } from '@reduxLocal/actions/types';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { TamaguiProvider, Theme } from 'tamagui';
import { config } from '../../tamagui.config';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';
import { PortalProvider } from '@tamagui/portal';
import { DefaultRootState } from '@reduxLocal/persistState'; // Ensure DefaultRootState is imported
import { trackUrlWithObjectId } from '@services/urlObjectIdTracker';

// Create a Root Stack Navigator that will conditionally render the Auth or App flow
const RootStack = Platform.OS === 'web' 
  ? createStackNavigator<RootStackParamList>() 
  : createNativeStackNavigator<RootStackParamList>();

const Navigation = ({ colorScheme, hasUpdatePending }: { colorScheme: ColorSchemeName, hasUpdatePending: boolean }) => {
  
  const dispatch = useDispatch();
  const theme = themeSelector();
  const insets = useSafeAreaInsets();
  const { isTablet } = useDeviceType();
  const cardBg = theme ? '#ffffff': '#333333';
  const navigationRef = useRef(null);

  
  // --- CRITICAL: Select authentication state from Redux ---
  const isAuthenticated = useSelector((state: DefaultRootState) => state.promiseData?.isAuthenticated || false); 
  const isLoadingAuth = useSelector((state: DefaultRootState) => state.promiseData?.loading || false);


  useEffect(() => {
    document.documentElement.style.setProperty('--bs-card-bg', cardBg);
  },[theme])

  // Log authentication state changes for debugging
  useEffect(() => {
    console.log('🔐 Auth State Changed:', {
      isAuthenticated,
      isLoadingAuth,
      timestamp: new Date().toISOString()
    });
  }, [isAuthenticated, isLoadingAuth]);

  // Function to call the generate payment link API
  const generatePaymentLink = async (productId: string, promoCode?: string) => {
    try {
      const url = new URL(`${USER_API_BASE_URL}/payments/generate-payment-link`);
      url.searchParams.set('productId', productId);
      if (promoCode) {
        url.searchParams.set('promoCode', promoCode);
      }

      console.log('🌐 API Base URL:', USER_API_BASE_URL);
      console.log('🌐 Full API URL:', url.toString());

      const requestOptions = {
        method: 'GET',
        credentials: 'include' as RequestCredentials, // Include session cookies
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      };
      

      const response = await fetch(url.toString(), requestOptions);



      const responseText = await response.text();
      console.log('📡 Raw API Response:', responseText);

      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          console.log('✅ Parsed response data:', data);
          console.log('🔗 Checkout URL:', data.checkoutUrl);
          return data.checkoutUrl;
        } catch (parseError) {
          console.error('💥 JSON Parse Error:', parseError);
          console.error('📄 Response text that failed to parse:', responseText);
          return null;
        }
      } else {
        console.error('❌ API Request Failed:', {
          status: response.status,
          statusText: response.statusText,
          responseBody: responseText
        });
        return null;
      }
    } catch (error) {
      console.error('💥 Network/Fetch Error:', error);
      console.error('🔍 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return null;
    }
  };

  // Debug function for testing payment link generation
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Add debug function to window for manual testing
      (window as any).debugPaymentLink = async (productId: string, code: string) => {
        console.log('🧪 Manual debug test started');
        console.log('🔍 Current auth state:', { isAuthenticated, isLoadingAuth });
        const result = await generatePaymentLink(productId, code);
        console.log('🧪 Debug result:', result);
        return result;
      };
      
      // Add function to check current URL params
      (window as any).checkUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlParams.entries());
        console.log('🔍 Current URL params:', params);
        return params;
      };

      console.log('🛠️ Debug functions added to window:');
      console.log('   - window.debugPaymentLink(productId, code)');
      console.log('   - window.checkUrlParams()');
    }
  }, [isAuthenticated, isLoadingAuth]);



  console.log('🔧 About to register payment link detection useEffect...');

  // Check for productId and code in URL params when user is authenticated
  useEffect(() => {
    console.log('🔍 Payment Link Effect Running:', {
      platform: Platform.OS,
      isAuthenticated,
      isLoadingAuth,
      windowLocation: typeof window !== 'undefined' ? window.location.href : 'No window'
    });

    if (Platform.OS === 'web') {
      
      if (isAuthenticated) {
        
        if (!isLoadingAuth) {
          
          const urlParams = new URLSearchParams(window.location.search);
          const productId = urlParams.get('productId');
          
          // Check for both 'code' and 'promoCode' parameters
          const code = urlParams.get('code') || urlParams.get('promoCode');
          
          console.log('📋 URL Parameters:', {
            fullUrl: window.location.href,
            search: window.location.search,
            productId,
            code,
            hasCode: !!urlParams.get('code'),
            hasPromoCode: !!urlParams.get('promoCode'),
            allParams: Object.fromEntries(urlParams.entries())
          });

          // Validate URL format
          if (window.location.search.includes('??')) {
            console.warn('⚠️ Invalid URL format detected - multiple question marks found');
            console.warn('💡 Expected format: ?productId=XXX&code=YYY');
            console.warn('💡 Current URL:', window.location.href);
          }

          if (productId && code) {
            console.log('🔄 Detected productId and code in URL params, generating payment link...');
            console.log('📋 ProductId:', productId, 'PromoCode:', code);
            
            generatePaymentLink(productId, code).then((checkoutUrl) => {
              if (checkoutUrl) {
                console.log('✅ Payment link generated, redirecting to:', checkoutUrl);
                
                // Clean up URL parameters before redirecting
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('productId');
                newUrl.searchParams.delete('code');
                newUrl.searchParams.delete('promoCode');
                
                console.log('🧹 Cleaning URL from:', window.location.href, 'to:', newUrl.toString());
                
                // Update browser history to clean URL without causing navigation
                window.history.replaceState({}, '', newUrl.toString());
                
                // Redirect to the checkout URL
                console.log('🚀 Redirecting to checkout URL...');
                window.location.href = checkoutUrl;
              } else {
                console.error('❌ Failed to generate payment link - checkoutUrl is null/undefined');
              }
            }).catch((error) => {
              console.error('💥 Promise rejection in generatePaymentLink:', error);
            });
          } else {
            console.log('ℹ️ No productId and code found in URL params');
            console.log('   - productId present:', !!productId);
            console.log('   - code present:', !!code);
          }
        } else {
          console.log('⏳ Still loading auth, skipping payment link check');
        }
      } else {
        console.log('🔒 User not authenticated, skipping payment link check');
      }
    } else {
      console.log('📱 Platform is not web, skipping payment link check');
    }
  }, [isAuthenticated, isLoadingAuth]);

  // Track URLs containing ObjectIds for context enrichment
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Only track URLs when user is authenticated
    if (!isAuthenticated || isLoadingAuth) return;

    // Track current URL on mount and when auth state changes
    const trackCurrentUrl = async () => {
      if (typeof window !== 'undefined' && window.location.href) {
        try {
          await trackUrlWithObjectId(window.location.href, {
            source: 'navigation',
            authenticated: isAuthenticated,
          });
        } catch (error) {
          // Silently fail - URL tracking is optional
          console.warn('Failed to track URL:', error);
        }
      }
    };

    // Track initial URL
    trackCurrentUrl();

    // Track URL changes via popstate (back/forward navigation)
    const handlePopState = async () => {
      await trackCurrentUrl();
    };

    // Override pushState and replaceState to track programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      // Use setTimeout to ensure URL is updated before tracking
      setTimeout(trackCurrentUrl, 0);
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      // Use setTimeout to ensure URL is updated before tracking
      setTimeout(trackCurrentUrl, 0);
    };

    // Listen to popstate events
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [isAuthenticated, isLoadingAuth]);

  // Handle navigation when auth state changes
  // NOTE: On web, we let LoadingScreen handle initial navigation after session validation
  // This effect only handles subsequent auth state changes (e.g., after logout) on native platforms
  useEffect(() => {
    // Skip auto-navigation on web - LoadingScreen handles it after session validation
    if (Platform.OS === 'web') {
      return;
    }

    // On native platforms, handle navigation normally
    if (navigationRef.current && !isLoadingAuth) {
      try {
        if (isAuthenticated) {
          navigationRef.current.navigate('Dashboard');
        } else {
          navigationRef.current.navigate('Auth');
        }
      } catch (error) {
        console.error('Navigation error:', error);
      }
    }
  }, [isAuthenticated, isLoadingAuth]);

  const navTheme = useMemo(() => ({
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      card: 'black',
      view: 'white',
      notification: 'white',
      text: 'black',
    },
  }), []);

  const darkTheme = useMemo(() => ({
    ...DarkTheme,
    colors: {
      ...DarkTheme.colors,
      text: 'white',
    }
  }), []);

  useEffect(() => {
    if (colorScheme != theme && Platform.OS != 'web') {
      dispatch({
        type: CHANGE_THEME, payload: {
          lightTheme: colorScheme === 'light' ? true : false,
          theme: colorScheme
        }
      });
    }
  }, [colorScheme, theme, dispatch]); // Added dispatch to dependency array

  return (
    <NavigationContainer 
      ref={navigationRef}
      documentTitle={{
        formatter: (options, route) => {
          // Use explicit screen title if provided, otherwise fallback to route name
          const pageTitle = options?.title ?? route.name;
          return `SmartSaaS - ${pageTitle}`;
        },
      }}
      linking={LinkingConfiguration}
      theme={colorScheme === 'dark' ? darkTheme : navTheme}
    >
      <TamaguiProvider defaultTheme={theme ? "light" : "dark"} config={config}>
        <PortalProvider shouldAddRootHost>
          <Theme>
            <BottomSheetModalProvider>
              {/* The main RootStack.Navigator that conditionally renders */}
              {/* Always start with Loading screen on web to validate session first */}
              <RootStack.Navigator 
                id="RootStack"
                initialRouteName={Platform.OS === 'web' ? "Loading" : (isLoadingAuth ? "Loading" : isAuthenticated ? "Dashboard" : "Auth")}
                screenOptions={{ headerShown: false, gestureEnabled: false }}
              >
                <RootStack.Screen name="Loading" component={LoadingScreen} />
                <RootStack.Screen name="Dashboard" component={DashboardNavigator} />
                <RootStack.Screen name="Auth" component={LoginAuthStack} />
              </RootStack.Navigator>

              {/* Modals that should float above the entire navigation stack */}
              <AuthModal />
              <TCModal/>
              <RegisterModal />
              <LoadingModal />
              
            </BottomSheetModalProvider>
          </Theme>
        </PortalProvider>
      </TamaguiProvider>
    </NavigationContainer>
  );
};

// --- NEW: Create a separate Login/Auth Stack ---
// This will contain all your authentication-related screens
const LoginAuthStack = () => {
  const LoginAuthStackInternal = Platform.OS === 'web'
    ? createStackNavigator<LoginStackParamList>()
    : createNativeStackNavigator<LoginStackParamList>();

  // Determine initial route based on URL - use useEffect to handle URL changes
  const [initialRoute, setInitialRoute] = React.useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname.includes('/learn')) {
        return 'Learn';
      }
    }
    return 'Login';
  });

  // Update route when URL changes
  React.useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname.includes('/learn')) {
        setInitialRoute('Learn');
      } else {
        setInitialRoute('Login');
      }
    }
  }, []);

  // Clean URL on mount if needed
  React.useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      const hasLearningModeParam = searchParams.has('learningMode');
      
      // If we're on /login and have learningMode param, remove it
      if (pathname.includes('/login') && hasLearningModeParam) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('learningMode');
        newUrl.searchParams.delete('learning');
        newUrl.searchParams.delete('learn');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, []);

  return (
    <LoginAuthStackInternal.Navigator
      initialRouteName={initialRoute}
      screenOptions={{ headerShown: false, gestureEnabled: false }}>
      <LoginAuthStackInternal.Screen 
        name="Login" 
        component={LoginScreen} 
        options={{ freezeOnBlur: true }}
      />
      <LoginAuthStackInternal.Screen 
        name="Learn" 
        component={LoginScreen} 
        options={{ freezeOnBlur: true }}
        listeners={{
          focus: () => {
            // Ensure URL stays as /learn when Learn screen is focused
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              const newUrl = new URL(window.location.href);
              if (newUrl.pathname !== '/learn') {
                newUrl.pathname = '/learn';
                // Remove any query params
                newUrl.search = '';
                window.history.replaceState({}, '', newUrl.toString());
              }
            }
          },
        }}
      />
      <LoginAuthStackInternal.Screen name="Registration" component={Registration} options={{ freezeOnBlur: true }} />

      {/* Add other authentication screens here if you have them */}
      {/* <LoginAuthStackInternal.Screen name="Register" component={RegisterScreen} /> */}
      {/* <LoginAuthStackInternal.Screen name="ForgotPassword" component={ForgotPasswordScreen} /> */}
    </LoginAuthStackInternal.Navigator>
  );
};

export default Navigation;
